import { apiGet } from '../../lib/api_v2';
import db from '../../lib/db';

import { isRecord } from '../core/record';

/** Legacy renderer/action row shape (snake_case fields, array attachments/reactions). */
export type MessageActionRow = Record<string, unknown>;

/** Which source satisfied a message-action lookup. */
export type MessageActionSource = 'feed' | 'network' | 'legacy';

/** Terminal resolution states; a pending promise is the loading state. */
export type MessageActionResolution =
  | { readonly state: 'found'; readonly source: MessageActionSource; readonly message: MessageActionRow }
  | { readonly state: 'not_found' }
  | { readonly state: 'error' };

export interface ResolveMessageForActionInput {
  readonly messageId: string | null | undefined;
  /** Channel scope for the message; required to enable the network fallback. */
  readonly channelId?: string | null;
  /** Opt-in API v2 point lookup for messages outside durable feed coverage. */
  readonly allowNetwork?: boolean;
  /** Explicit legacy `messages` compatibility read — optimistic/pseudotype rows only. */
  readonly allowLegacy?: boolean;
}

/** Lookup overrides used by tests or alternate integrations. */
export interface MessageActionLookups {
  readonly feedLookup?: (id: string) => Promise<unknown | null | undefined>;
  readonly legacyLookup?: (id: string) => Promise<unknown | null | undefined>;
  readonly networkLookup?: (channelId: string, id: string) => Promise<unknown | null | undefined>;
}

// Feed-sync bookkeeping fields that must not leak into rows handed to legacy renderers.
const FEED_INTERNAL_FIELDS = ['_feed_mutation_cursor', 'fresh_since'] as const;

/**
 * Network resolutions are shared and remembered, because the callers that opt into the network
 * fallback render lists: an Activity Feed of N mentions is N subjects in channels the user may
 * never have opened, and it refires every lookup on each remount — including for ids the server
 * has already answered 404 for.
 *
 * Staleness is bounded by the source order rather than by a TTL. `feed_messages` is consulted
 * first, so anything inside durable feed coverage is served live and never reads this cache at
 * all; what gets cached is only messages outside coverage entirely, which receive no live
 * updates through any path.
 */
const NETWORK_CACHE_LIMIT = 200;
const networkResolutions = new Map<string, MessageActionRow | null>();
const networkRequests = new Map<string, Promise<MessageActionRow | null>>();

// SAFETY: The legacy Dexie singleton is a JS module; only point-read surfaces are used here.
const database = db as unknown as {
  readonly feed_messages: { get(id: string): Promise<unknown | null | undefined> };
  readonly messages: { get(id: string): Promise<unknown | null | undefined> };
};

/** Strips feed-sync bookkeeping from a durable feed row, yielding the legacy action shape. */
export function normalizeFeedMessageRow(row: unknown): MessageActionRow | null {
  if (!isRecord(row)) return null;
  const normalized: Record<string, unknown> = { ...row };
  for (const field of FEED_INTERNAL_FIELDS) delete normalized[field];
  return normalized;
}

/**
 * Point-reads a server message from the durable feed cache. Safe inside useLiveQuery:
 * only `feed_messages` is touched, so consumers re-render on feed writes, and a storage
 * failure degrades to a miss — a rejected read must never throw through a liveQuery into
 * the feed error boundary while the consumer holds a perfectly good retained row.
 * (resolveMessageForAction's own feed lookup still reports storage failure distinctly.)
 */
export async function feedMessageById(messageId: string | null | undefined): Promise<MessageActionRow | null> {
  if (!messageId) return null;
  try {
    return normalizeFeedMessageRow(await database.feed_messages.get(String(messageId)));
  } catch {
    return null;
  }
}

/**
 * Canonical lookup for UI that needs a server message by id when no rendered row is
 * in hand (callers holding one use it directly — see useRetainedFeedMessage).
 *
 * Source order: durable `feed_messages`, API v2 point read (opt-in), then legacy
 * `messages` (explicit opt-in for optimistic/pseudotype compatibility only). When
 * both stores hold the same server id the feed row wins.
 */
export async function resolveMessageForAction(
  input: ResolveMessageForActionInput,
  lookups: MessageActionLookups = {}
): Promise<MessageActionResolution> {
  const messageId = input.messageId ? String(input.messageId) : null;
  if (!messageId) return { state: 'not_found' };

  const feedLookup = lookups.feedLookup ?? defaultFeedLookup;
  const legacyLookup = lookups.legacyLookup ?? defaultLegacyLookup;
  const networkLookup = lookups.networkLookup ?? defaultNetworkLookup;
  let lookupFailed = false;

  try {
    const feedRow = normalizeFeedMessageRow(await feedLookup(messageId));
    if (feedRow) return { state: 'found', source: 'feed', message: feedRow };
  } catch {
    lookupFailed = true;
  }

  if (input.allowNetwork && input.channelId) {
    try {
      const networkRow = await resolveNetworkRow(String(input.channelId), messageId, networkLookup);
      if (networkRow) return { state: 'found', source: 'network', message: networkRow };
    } catch {
      lookupFailed = true;
    }
  }

  if (input.allowLegacy) {
    try {
      const legacyRow = await legacyLookup(messageId);
      if (isRecord(legacyRow)) return { state: 'found', source: 'legacy', message: legacyRow };
    } catch {
      lookupFailed = true;
    }
  }

  return lookupFailed ? { state: 'error' } : { state: 'not_found' };
}

/** Drops the network resolution cache. Exported so tests don't leak resolutions into each other. */
export function resetMessageActionNetworkCache(): void {
  networkResolutions.clear();
  networkRequests.clear();
}

/** Shares one request per (channel, message) and remembers its terminal answer; errors are not cached. */
async function resolveNetworkRow(
  channelId: string,
  messageId: string,
  networkLookup: (channelId: string, id: string) => Promise<unknown | null | undefined>
): Promise<MessageActionRow | null> {
  const key = `${channelId}:${messageId}`;
  if (networkResolutions.has(key)) return networkResolutions.get(key) ?? null;

  const inFlight = networkRequests.get(key);
  if (inFlight) return inFlight;

  const request = networkLookup(channelId, messageId)
    .then((row) => {
      const resolved = isRecord(row) ? row : null;
      // Bounded, evicting oldest-inserted first. Close enough to LRU for callers that resolve a
      // list of subjects once and re-read those same ids on every later mount.
      if (networkResolutions.size >= NETWORK_CACHE_LIMIT) {
        const oldest = networkResolutions.keys().next().value;
        if (oldest !== undefined) networkResolutions.delete(oldest);
      }
      networkResolutions.set(key, resolved);
      return resolved;
    })
    .finally(() => {
      networkRequests.delete(key);
    });

  networkRequests.set(key, request);
  return request;
}

async function defaultFeedLookup(id: string): Promise<unknown | null | undefined> {
  return database.feed_messages.get(id);
}

async function defaultLegacyLookup(id: string): Promise<unknown | null | undefined> {
  return database.messages.get(id);
}

// Only a genuine 404 may resolve as not-found; any other HTTP failure must surface as a
// lookup error so a server outage is never reported to the user as a missing message.
async function defaultNetworkLookup(channelId: string, id: string): Promise<unknown | null | undefined> {
  const response = await apiGet(`/channels/${channelId}/messages/${encodeURIComponent(id)}`);
  if (response.status === 404) return null;
  if (!response.ok) throw new Error(`Message point lookup failed with HTTP ${response.status}`);
  const data: unknown = await response.json();
  if (!isRecord(data) || data.ok !== true) return null;
  const envelopeData = isRecord(data.data) ? data.data : null;
  return envelopeData?.message ?? null;
}
